import { SadieSimulatorEntry, SadieSimulatorTurn } from "@/types";

// A rendered prompt's sections, split at each "## " heading.
const splitSections = (prompt: string): string[] => prompt.split(/\n(?=## )/);

const formatEnvelope = (turn: SadieSimulatorTurn): string =>
  [
    turn.disposition,
    `intent=${turn.intentLevel ?? "null"}`,
    turn.optOut ? "OPT OUT" : null,
    turn.jobInterest ? `job=${turn.jobInterest}` : null,
    turn.referral
      ? `referral=${turn.referral.name} ${turn.referral.contact}`
      : null,
    turn.loopedToSession ? `looped to ${turn.loopedToSession}` : null,
    turn.staffAlert ? `staff alert: ${turn.staffAlert}` : null,
    turn.pitchFeedback ? `pitch feedback: ${turn.pitchFeedback}` : null,
    turn.pipelineNote ? `pipeline note: ${turn.pipelineNote}` : null,
  ]
    .filter(Boolean)
    .join(", ");

export const sadieSimulator = {
  formatTranscript: (
    entries: SadieSimulatorEntry[],
    agentName: string,
  ): string =>
    entries
      .map((entry) => {
        const speaker = entry.role === "assistant" ? agentName : "Worker";
        const body =
          entry.content.trim().length === 0 ? "(silent)" : entry.content;
        const envelope = entry.turn ? `  [${formatEnvelope(entry.turn)}]` : "";
        const search = entry.search ? `(${entry.search.summary})\n` : "";
        return `${search}${speaker}: ${body}${envelope}`;
      })
      .join("\n"),

  // A line break in the agent's reply is the boundary between texts. Each
  // piece is its own bubble, the way it would arrive on a phone.
  splitPieces: (content: string): string[] =>
    content
      .split(/\r?\n/)
      .map((piece) => piece.trim())
      .filter((piece) => piece.length > 0),

  // Carries a re-render into an edited prompt. The server re-renders the
  // whole prompt when the setup behind it changes, which would throw away
  // the tester's edits. Only the sections that differ between the two
  // renders are swapped, each where it sits in the edited prompt. Null when
  // that cannot be done safely: the sections no longer line up, or the
  // edits touched a section that changed. The caller then falls back to the
  // fresh render and says so.
  spliceRenderChange: (
    previousRender: string,
    nextRender: string,
    edited: string,
  ): string | null => {
    if (edited === previousRender) return nextRender;
    const before = splitSections(previousRender);
    const after = splitSections(nextRender);
    if (before.length !== after.length) return null;
    let result = edited;
    for (let i = 0; i < before.length; i++) {
      if (before[i] === after[i]) continue;
      const at = result.indexOf(before[i]);
      if (at === -1 || result.includes(before[i], at + 1)) return null;
      result =
        result.slice(0, at) + after[i] + result.slice(at + before[i].length);
    }
    return result;
  },
};
